import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Wraps any content and animates it into view (fade + rise) the first time
 * it scrolls into the viewport. Pure CSS/IntersectionObserver — no extra deps.
 *
 * Props:
 *  - delay:  ms delay before the animation starts (for staggering)
 *  - y:      px the element rises from (default 28)
 *  - once:   only animate the first time it becomes visible (default true)
 */
export default function ScrollReveal({ children, delay = 0, y = 28, once = true, sx = {}, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : `translateY(${y}px) scale(0.98)`,
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        willChange: 'opacity, transform',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
